import { useState, useEffect } from 'react';

interface VersionInfo {
  version: string;
  timestamp: string;
}

interface HealthInfo {
  status: string;
  timestamp: string;
  version: string;
  environment: string;
  services: any;
}

export interface AppInfo {
  version: string | null;
  environment: string | null;
}

/**
 * Hook to fetch and cache the application version and environment from the backend API
 * Information is fetched once on first use and cached for the session
 */
export function useVersion(): string | null {
  // Hydrate from sessionStorage for instant first paint; the background
  // fetch below always runs so a stale cache (e.g. tab opened before the
  // latest deploy) gets corrected on next mount.
  const [version, setVersion] = useState<string | null>(() =>
    sessionStorage.getItem('app_version'),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/version');
        if (!response.ok || cancelled) return;
        const data: VersionInfo = await response.json();
        if (cancelled) return;
        sessionStorage.setItem('app_version', data.version);
        setVersion((prev) => (prev === data.version ? prev : data.version));
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch app version:', error);
        setVersion((prev) => prev ?? 'Unknown');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return version;
}

/**
 * Hook to fetch and cache both version and environment from the health endpoint
 * Information is fetched once on first use and cached for the session
 */
export function useAppInfo(): AppInfo {
  const [appInfo, setAppInfo] = useState<AppInfo>(() => ({
    version: sessionStorage.getItem('app_version'),
    environment: sessionStorage.getItem('app_environment'),
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/health');
        if (!response.ok || cancelled) return;
        const data: HealthInfo = await response.json();
        if (cancelled) return;
        sessionStorage.setItem('app_version', data.version);
        sessionStorage.setItem('app_environment', data.environment);
        setAppInfo((prev) =>
          prev.version === data.version && prev.environment === data.environment
            ? prev
            : { version: data.version, environment: data.environment },
        );
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch app info:', error);
        setAppInfo((prev) => ({
          version: prev.version ?? 'Unknown',
          environment: prev.environment ?? 'unknown',
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return appInfo;
}